import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BarcodeCamera } from "@/components/recharge/BarcodeCamera";
import { X, RotateCcw, Loader2 } from "lucide-react";

export default function TicketScanSearch({ onClose }) {
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState("scanning");
  const [error, setError] = useState("");

  const onResult = async (code) => {
    setStatus("looking");
    try {
      const { data } = await api.get(`/lookup/${encodeURIComponent(code.trim())}`);
      navigate(`/tickets/${data.ticket.id}`);
    } catch (e) {
      setError(e.response?.status === 404 ? "Ticket not found — this barcode doesn't match any ticket." : "Could not look up the ticket. Try again.");
      setStatus("error");
    }
  };

  const retry = () => { setError(""); setStatus("scanning"); setAttempt((a) => a + 1); };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="ticket-scan-modal">
      <div className="bg-[#131D33] border border-slate-700 rounded-2xl w-full max-w-md p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Scan Ticket Barcode</div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1" aria-label="Close" data-testid="ticket-scan-close"><X /></button>
        </div>
        {status === "scanning" && (
          <BarcodeCamera key={attempt} onResult={onResult}
            onError={() => { setError("Camera unavailable — allow camera access or search manually."); setStatus("error"); }} />
        )}
        {status === "looking" && (
          <div className="flex items-center justify-center gap-2 text-slate-300 py-10" data-testid="ticket-scan-looking">
            <Loader2 className="animate-spin" size={18} /> Looking up ticket…
          </div>
        )}
        {status === "error" && (
          <div className="rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-sm p-3" data-testid="ticket-scan-error">{error}</div>
        )}
        <p className="text-xs text-slate-500">Hold the ticket's barcode inside the box. Lookup only — nothing on the ticket changes.</p>
        <div className="flex gap-2">
          {status === "error" && (
            <Button onClick={retry} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold" data-testid="ticket-scan-retry">
              <RotateCcw size={14} className="mr-1" /> Scan Another
            </Button>
          )}
          <Button variant="outline" onClick={onClose} className="flex-1 bg-slate-900 border-slate-700 text-slate-200" data-testid="ticket-scan-manual">
            Search Manually
          </Button>
        </div>
      </div>
    </div>
  );
}
